import type { Locale } from '@/lib/i18n';

/**
 * Number formatting for the whole UI: money, percentages and counts.
 *
 * One space convention everywhere: a narrow no-break space (U+202F) groups
 * thousands and sits before "zł" and "%". Polish grouping is applied from four
 * digits (`1 234`), not from five as `pl-PL` in `Intl` does by default.
 *
 * ## Money that arrives as a string
 *
 * Amounts that are `numeric(14,2)` on the server arrive as decimal strings and
 * are formatted here by string manipulation, so they never become a double. A
 * `Number()` here invites a `Number()` in a component that then adds two of
 * them. The helpers that take a `number` are for figures the API already sends
 * as numbers.
 */

/**
 * A narrow no-break space, written as an escape rather than as a literal.
 *
 * `\u202F` and not a typed character: a literal non-breaking space is invisible
 * in an editor and indistinguishable from a normal one in a diff, which is why
 * eslint's `no-irregular-whitespace` refuses it. No-break so a figure never
 * wraps between its thousands.
 */
export const GROUP_SEPARATOR = '\u202F';
const DECIMAL_SEPARATOR = ',';

/**
 * `"1234.50"` → `"1 234,50 zł"`.
 *
 * Negative amounts keep their sign: a correction reduces revenue and a figure
 * that hid its sign would read as a gain.
 */
export const formatAmount = (value: string): string =>
  `${formatNumber(value)}${GROUP_SEPARATOR}zł`;

/** The same, without the currency — for a column whose header carries it. */
export const formatNumber = (value: string): string => {
  const negative = value.startsWith('-');
  const [whole = '0', fraction = '00'] = value.replace('-', '').split('.');

  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEPARATOR);
  const cents = fraction.padEnd(2, '0').slice(0, 2);

  return `${negative ? '−' : ''}${grouped}${DECIMAL_SEPARATOR}${cents}`;
};

/**
 * `"1234.50"` → `"1 235"`. Nearest złoty, no grosze.
 *
 * The daily summary is a morning picture, not a ledger: grosze on every row
 * make the eye do rounding the screen should have done. 50 groszy goes up.
 * Worked in grosze as integers so `"999.99"` becomes `"1 000"` without a
 * double ever seeing the amount. The % still uses the exact strings.
 */
export const formatZloty = (value: string): string => {
  const negative = value.startsWith('-');
  const [whole = '0', fraction = '00'] = value.replace('-', '').split('.');
  const grosze = BigInt(whole || '0') * 100n + BigInt(fraction.padEnd(2, '0').slice(0, 2));
  const zloty = (grosze + 50n) / 100n;
  const grouped = String(zloty).replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEPARATOR);

  return `${negative && zloty !== 0n ? '−' : ''}${grouped}`;
};

/** The same, with the currency — for a headline. */
export const formatZlotyAmount = (value: string): string =>
  `${formatZloty(value)}${GROUP_SEPARATOR}zł`;

/**
 * Percentage change between two amounts, or null when there is no baseline.
 *
 * Null rather than 0 or Infinity when `previous` is zero: "up from nothing" is
 * not a percentage, and rendering ∞ or 0 % both state something false. The
 * caller shows a dash.
 *
 * This one **does** use numbers, deliberately and locally: a ratio is not money,
 * it is rounded to a whole percent for display, and nothing downstream adds it
 * to anything.
 */
export const percentChange = (current: string, previous: string): number | null => {
  const before = Number(previous);
  const after = Number(current);

  if (!Number.isFinite(before) || !Number.isFinite(after) || before === 0) {
    return null;
  }

  return Math.round(((after - before) / Math.abs(before)) * 100);
};

/**
 * A whole number with its thousands grouped: `2135374` → `2 135 374`.
 *
 * Counts sit beside money on the reference screen, and an ungrouped
 * `2135374 wierszy` is a wall of digits the eye cannot size while
 * `36 707 986,55 zł` next to it is legible. Same separator, so the two line up.
 */
export const formatCount = (value: number): string =>
  String(Math.trunc(value)).replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEPARATOR);

/** The `Intl` locale for a UI locale. */
export const toIntlLocale = (locale: Locale): 'pl-PL' | 'en-GB' =>
  locale === 'pl' ? 'pl-PL' : 'en-GB';

/**
 * A number with Polish grouping and decimal comma: `1234.5` → `1 234,5`.
 *
 * Fixed to Polish, not the UI locale: the figures are reconciled against Polish
 * spreadsheets and payroll software, and an English UI must not regroup them.
 */
export const formatDecimal = (
  value: number,
  maximumFractionDigits = 0,
  minimumFractionDigits = 0,
): string =>
  value
    .toLocaleString('pl-PL', {
      useGrouping: 'always',
      minimumFractionDigits,
      maximumFractionDigits,
    })
    .replace(/[\u00A0\u202F ]/g, GROUP_SEPARATOR);

/** Nearest whole złoty with the currency: `1234.6` → `1 235 zł`. */
export const formatWholeZloty = (value: number): string =>
  `${formatDecimal(Math.round(value))}${GROUP_SEPARATOR}zł`;

/** A value that is already a percent: `12.5` → `12,5 %`. Trailing zeros dropped. */
export const formatPercent = (
  value: number,
  maximumFractionDigits = 0,
  minimumFractionDigits = 0,
): string => `${formatDecimal(value, maximumFractionDigits, minimumFractionDigits)}${GROUP_SEPARATOR}%`;

/**
 * An etat decimal as the fraction people say — `0.5` → `1/2`.
 *
 * Eighths, because a working day is eight hours and a part-time post is quoted
 * against it. Anything that is not a clean eighth falls back to the decimal rather
 * than being forced into a fraction that would misstate it.
 */
export const formatEtat = (fraction: number): string => {
  const eighths = Math.round(fraction * 8);

  if (Math.abs(fraction * 8 - eighths) > 0.001 || eighths < 1 || eighths > 8) {
    return String(fraction);
  }

  if (eighths === 8) {
    return '1/1';
  }

  // 4/8 reads as 1/2 and 6/8 as 3/4 — the forms people actually use.
  if (eighths === 4) {
    return '1/2';
  }

  if (eighths === 6) {
    return '3/4';
  }

  if (eighths === 2) {
    return '1/4';
  }

  return `${eighths}/8`;
};
